import { useReducer, useEffect, useContext, useMemo } from 'react'
import {
    TextField,
    Button,
    Chip,
    debounce,
    List,
    ListItem,
    IconButton,
    ListItemAvatar,
    Avatar,
    ListItemText,
    Box,
    Tabs,
    Tab,
    Typography,
    Toolbar,
    Stack,
} from '@mui/material'
import {
    Grid,
    FormSection,
    UniversalAutoComplete,
    UniversalAccordion,
} from '../../../UniversalComponents'
import DealerProfileUsers from './DealerProfileUsers'
import { ToastMessage } from '../../../../context/ToastPopUpContext'
import responseError from '../../../../context/responseError/responseError'

import { addDealerProfile, getDealerQuery } from '../../../../apiRoutes'
import DeleteIcon from '@mui/icons-material/Delete'
import CircleIcon from '@mui/icons-material/Circle'
import MapsHomeWorkIcon from '@mui/icons-material/MapsHomeWork'
import AddBoxIcon from '@mui/icons-material/AddBox'
import { useGlobalState } from '../../../../context/GlobalStateContext'

import { getDealerProfileUsers } from '../../../../apiRoutes/dealerProfileRoutes'
import PersonIcon from '@mui/icons-material/Person'

const today = new Date().toISOString().substring(0, 10)
interface DealerProfileProps {
    formTitle: string
    row: any
    onClose: any
    isDealerView?: any
}

const DealerProfileForm = ({
    formTitle,
    row,
    onClose,
    isDealerView,
}: DealerProfileProps) => {
    const defaultState = {
        formData: {
            id: row?.id || '',
            dealerProfileName: row?.dealerProfileName || '',
            manufacturers: row?.manufacturers || [],
            dealers: row?.dealers || [],
            dealerName: row?.dealerName || '',
            dealerEmail: row?.dealerEmail || '',
            dealerPhoneNumber: row?.dealerPhoneNumber || '',
            dealerAddress: row?.dealerAddress || '',
            dealerSalesContactName: row?.dealerSalesContactName || '',
            dealerSalesContactEmail: row?.dealerSalesContactEmail || '',
            dealerSalesContactPhone: row?.dealerSalesContactPhone || '',
            dealerServiceContactName: row?.dealerServiceContactName || '',
            dealerServiceContactEmail: row?.dealerServiceContactName || '',
            dealerServiceContactPhone: row?.dealerServiceContactPhone || '',
            dealerNotificationUsers: row?.dealerNotificationUsers || [],
        },

        formValidation: {
            isFormValid: false,
            isFormDirty: false,
            dealerProfileNameValidation: false,
            dealersValidation: false,
        },
        manufacturers: [],
        manufacturerNames: [],
        dealerQuery: '',
        dealerOptions: [],
        findDealer: '',
        tabValue: 0,
        findUser: '',
        dealerAssignedUsers: [],
    }

    const reducer = (state, newState) => ({ ...state, ...newState })
    const setToastMessage = useContext(ToastMessage)
    const [state, setState] = useReducer(reducer, defaultState)

    const {
        formValidation,
        formData,
        dealerQuery,
        dealerOptions,
        findDealer,
        tabValue,
        dealerAssignedUsers,
        findUser,
    } = state

    const {
        isFormDirty,
        isFormValid,
        dealerProfileNameValidation,
        dealersValidation,
    } = formValidation

    const {
        dealerProfileName,
        manufacturers,
        dealers,
        dealerName,
        dealerEmail,
        dealerPhoneNumber,
        dealerAddress,
        dealerSalesContactName,
        dealerSalesContactEmail,
        dealerSalesContactPhone,
        dealerServiceContactName,
        dealerServiceContactEmail,
        dealerServiceContactPhone,
        dealerNotificationUsers,
    } = formData

    const { globalState } = useGlobalState()
    const { oupUser } = globalState
    const { userProfile, activeManufacturer, userEmail } = oupUser

    const isAdmin = userProfile?.superAdmin

    interface CustomTabPanelProps {
        children?: React.ReactNode
        value: number
        index: number
    }

    const CustomTabPanel = (props: CustomTabPanelProps) => {
        const { children, value, index, ...other } = props

        return (
            <div
                role="tabpanel"
                hidden={value !== index}
                id={`simple-tabpanel-${index}`}
                aria-labelledby={`simple-tab-${index}`}
                {...other}
            >
                {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
            </div>
        )
    }

    const handleChange = (e: any) => {
        const { name, value } = e.target

        setState({
            formData: { ...formData, [name]: value },
        })
    }
    const handleUserChange = (e: any) => {
        const { name, value } = e.target

        if (!value || !value._id) {
            return
        }

        const user = {
            _id: value._id,
            userEmail: value?.userEmail,
            userFirstName: value?.userFirstName,
            userLastName: value?.userLastName,
        }

        const doesUserExist = dealerNotificationUsers?.some(
            (d) => d._id === user._id
        )

        if (!doesUserExist) {
            setState({
                formData: {
                    ...formData,
                    dealerNotificationUsers: [...dealerNotificationUsers, user],
                },
                findUser: '',
            })
        }
    }

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setState({ tabValue: newValue })
    }

    const onDealerQueryChange = (e: any) => {
        const { value } = e.target
        let getManufacturers: any = []

        if (!value || !value._id) {
            return
        }

        const dealer = {
            dealerName: value.dealerName,
            _id: value._id,
            dealerEmail: value?.dealerEmail,
            dealerPhoneNumber: value?.dealerPhoneNumber,
            dealerAddress: value?.dealerAddress,
            dealerSalesContactName: value?.dealerSalesContactName,
            dealerSalesContactEmail: value?.dealerSalesContactEmail,
            dealerSalesContactPhone: value?.dealerSalesContactPhone,
            dealerServiceContactName: value?.dealerServiceContactName,
            dealerServiceContactEmail: value?.dealerServiceContactEmail,
            dealerServiceContactPhone: value?.dealerServiceContactPhone,
            manufacturer: value?.manufacturer,
        }

        if (Array.isArray(dealer?.manufacturer)) {
            const newMfrs = dealer.manufacturer.filter(
                (incoming) =>
                    incoming?._id &&
                    !manufacturers.some(
                        (existing) => existing._id === incoming._id
                    )
            )
            getManufacturers = [...manufacturers, ...newMfrs]
        }

        const doesDealerExist = dealers?.some((d) => d._id === dealer._id)

        if (!doesDealerExist) {
            setState({
                formData: {
                    ...formData,
                    dealers: [...dealers, dealer],
                    manufacturers: getManufacturers,
                },
                tabValue: tabValue + 1,
                findDealer: '',
            })
        }
    }

    const onDealerDelete = (id: string) => {
        const currentDealers = dealers ?? []
        const currentManufacturers = manufacturers ?? []

        const remainingDealers = currentDealers.filter(
            (dealer) => dealer._id !== id
        )

        if (remainingDealers.length === currentDealers.length) {
            console.warn(`Dealer with id ${id} not found`)
            return
        }

        const stillUsedIds = new Set<string>()

        remainingDealers.forEach((dealer) => {
            const mfrData = dealer.manufacturer

            if (Array.isArray(mfrData)) {
                mfrData.forEach((mfr) => {
                    if (mfr?._id) stillUsedIds.add(mfr._id)
                })
            } else if (mfrData && typeof mfrData === 'object' && mfrData._id) {
                stillUsedIds.add(mfrData._id)
            } else if (typeof mfrData === 'string') {
                stillUsedIds.add(mfrData)
            }
        })

        const updatedManufacturers = currentManufacturers.filter(
            (mfr) => mfr?._id && stillUsedIds.has(mfr._id)
        )

        setState({
            formData: {
                ...formData,
                dealers: remainingDealers,
                manufacturers: updatedManufacturers,
            },
            formValidation: {
                ...formValidation,
                isFormDirty: true,
            },
        })
    }

    const onUserDelete = (id: string) => {
        const currentUsers = dealerNotificationUsers ?? []

        const remainingUsers = dealerNotificationUsers.filter(
            (user) => user._id !== id
        )

        if (remainingUsers.length === currentUsers.length) {
            console.warn(`User with id ${id} not found`)
            return
        }

        const stillUsedIds = new Set<string>()

        remainingUsers.forEach((user) => {
            const mfrData = user.user_id

            if (Array.isArray(mfrData)) {
                mfrData.forEach((mfr) => {
                    if (mfr?._id) stillUsedIds.add(mfr._id)
                })
            } else if (mfrData && typeof mfrData === 'object' && mfrData._id) {
                stillUsedIds.add(mfrData._id)
            } else if (typeof mfrData === 'string') {
                stillUsedIds.add(mfrData)
            }
        })

        setState({
            formData: {
                ...formData,
                dealerNotificationUsers: remainingUsers,
            },
            formValidation: {
                ...formValidation,
                isFormDirty: true,
            },
        })
    }

    const debouncedSearch = useMemo(
        () =>
            debounce((value: string, queryKey: string) => {
                setState({ [queryKey]: value })
            }, 600),
        []
    )

    const handleSubmit = async () => {
        try {
            const parsedFormData = { ...formData }
            let res: any

            res = await addDealerProfile(parsedFormData)

            if (res) {
                const { data, status } = res

                if (data && status === 200) {
                    setToastMessage(
                        `Dealer Profile: ${data.dealerProfileName} was ${
                            row.id ? 'edited' : 'added'
                        } successfully`,
                        'success'
                    )
                    onClose()
                    if (isDealerView && isDealerView === true) {
                        setState({
                            formValidation: defaultState.formValidation,
                        })
                    }
                } else {
                    responseError(res, row)
                }
            }
        } catch (err: any) {
            console.error(err, 'error')
            setToastMessage(`${err}`, 'error')
        }
    }

    const validateOnBlur = (e: any) => {
        const { name, value } = e.target

        if (value !== '') {
            setState({
                formValidation: {
                    ...formValidation,
                    [name + 'Validation']: false,
                    isFormDirty: true,
                },
            })
        } else {
            setState({
                formValidation: {
                    ...formValidation,
                    [name + 'Validation']: true,
                    isFormDirty: true,
                },
            })
        }
    }
    useEffect(() => {
        if (
            isFormDirty &&
            !dealerProfileNameValidation &&
            !dealersValidation &&
            dealerProfileName &&
            dealers?.length !== 0
        ) {
            setState({
                formValidation: {
                    ...formValidation,
                    isFormValid: true,
                },
            })
        } else {
            setState({
                formValidation: {
                    ...formValidation,
                    isFormValid: false,
                },
            })
        }
    }, [
        isFormDirty,
        dealerProfileNameValidation,
        dealersValidation,
        dealerProfileName,
        dealers,
    ])
    const handleReset = () =>
        setState({
            formData: defaultState.formData,
            formValidation: defaultState.formValidation,
        })

    const getData = async () => {
        try {
            let users = await getDealerProfileUsers(row?.id)

            if (!users || users.length === 0) {
                setState({ dealerAssignedUsers: [] })
                return
            }

            const searchResults = users.map((item: any) => ({
                _id: item?._id,
                userEmail: item?.userEmail,
                userFirstName: item?.userFirstName,
                userLastName: item?.userLastName,
            }))

            if (searchResults && searchResults.length !== 0) {
                setState({
                    dealerAssignedUsers: searchResults,
                })
            }
        } catch (err: any) {
            setToastMessage(err, 'error')
            console.error(err, 'error')
        }
    }

    useEffect(() => {
        const getSearchedDealers = async () => {
            try {
                const searchedDealers = await getDealerQuery({
                    active: true,
                    search: dealerQuery,
                    limit: 20,
                    doNotSendPreliminarySuggestions: true,
                })

                if (!searchedDealers || searchedDealers.length === 0) {
                    setState({ dealerOptions: [] })
                    return
                }

                const searchResults = searchedDealers.map((item: any) => ({
                    _id: item?._id,
                    dealerName: item?.dealerName,
                    label: item?.dealerName,
                    dealerEmail: item?.dealerEmail,
                    dealerPhoneNumber: item?.dealerPhoneNumber,
                    dealerAddress: item?.dealerAddress,
                    dealerSalesContactName: item?.dealerSalesContactName,
                    dealerSalesContactEmail: item?.dealerSalesContactEmail,
                    dealerSalesContactPhone: item?.dealerSalesContactPhone,
                    dealerServiceContactName: item?.dealerServiceContactName,
                    dealerServiceContactEmail: item?.dealerServiceContactEmail,
                    dealerServiceContactPhone: item?.dealerServiceContactPhone,
                    manufacturer: item?.manufacturer,
                }))

                if (searchResults && searchResults.length !== 0) {
                    setState({
                        dealerOptions: searchResults,
                    })
                }
            } catch (err: any) {
                setToastMessage(err, 'error')
            }
        }

        getSearchedDealers()
    }, [dealerQuery])

    useEffect(() => {
        if (tabValue >= dealers.length) {
            setState({ tabValue: dealers.length - 1 })
        }
    }, [dealers])
    useEffect(() => {
        if (row?.id) {
            getData()
        }
    }, [])

    useEffect(() => {
        if (dealers.length === 0) {
            setState({ tabValue: 0 })
            return
        }

        if (tabValue >= dealers.length || tabValue < 0) {
            setState({ tabValue: 0 })
        }
    }, [dealers, tabValue])

    const safeTabValue =
        dealers.length === 0
            ? 0
            : Math.max(0, Math.min(tabValue, dealers.length - 1))

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} sx={{ padding: 1, marginTop: 4 }}>
                <Grid container spacing={2}>
                    <Grid item xs={7} md={7}>
                        <FormSection
                            title="Dealer Profile Contact Information"
                            titleAlignment="left"
                        >
                            <Grid item xs={12} md={12}>
                                <TextField
                                    fullWidth
                                    label="Dealer Profile Name"
                                    name="dealerProfileName"
                                    onChange={handleChange}
                                    required={true}
                                    value={dealerProfileName}
                                    onBlur={validateOnBlur}
                                    error={
                                        dealerProfileNameValidation &&
                                        !dealerProfileName
                                    }
                                    helperText={
                                        dealerProfileNameValidation &&
                                        !dealerProfileName
                                            ? 'Please enter a Dealer Profile Name'
                                            : ''
                                    }
                                    disabled={!isAdmin}
                                />
                            </Grid>
                            <Grid item xs={12} md={12}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <TextField
                                            label="Dealer Name"
                                            name="dealerName"
                                            onChange={handleChange}
                                            value={dealerName}
                                            onBlur={validateOnBlur}
                                            fullWidth
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <TextField
                                            label="Email"
                                            name="dealerEmail"
                                            value={dealerEmail}
                                            fullWidth
                                            disabled={true}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            label="Dealer Phone Number"
                                            name="dealerPhoneNumber"
                                            value={dealerPhoneNumber}
                                            fullWidth
                                            disabled={true}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            label="Sales Contact Name"
                                            name="dealerSalesContactName"
                                            value={dealerSalesContactName}
                                            fullWidth
                                            disabled={true}
                                        />
                                    </Grid>

                                    <Grid item xs={6}>
                                        <TextField
                                            label="Sales Contact Email"
                                            name="dealerSalesContactEmail"
                                            value={dealerSalesContactEmail}
                                            fullWidth
                                            disabled={true}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            label="Sales Contact Phone"
                                            name="dealerSalesContactPhone"
                                            value={dealerSalesContactPhone}
                                            fullWidth
                                            disabled={true}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            label="Service Contact Name"
                                            name="dealerServiceContactName"
                                            value={dealerServiceContactName}
                                            fullWidth
                                            disabled={true}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            label="Service Contact Email"
                                            name="dealerServiceContactEmail"
                                            value={dealerServiceContactEmail}
                                            fullWidth
                                            disabled={true}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            label="Service Contact Phone"
                                            name="dealerServiceContactPhone"
                                            value={dealerServiceContactPhone}
                                            fullWidth
                                            disabled={true}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </FormSection>
                        {row?.id && (
                            <Grid item xs={12} md={12}>
                                <FormSection
                                    title="Dealer Profile Assigned Users"
                                    titleAlignment="left"
                                >
                                    <Grid item xs={12}>
                                        <DealerProfileUsers
                                            dealerProfileId={row.id}
                                        />
                                    </Grid>
                                </FormSection>
                            </Grid>
                        )}
                        <Grid item xs={12} md={12}>
                            {row?.id && (
                                <Grid item xs={12}>
                                    <FormSection
                                        title="Notification Management"
                                        titleAlignment="left"
                                    >
                                        <Grid item xs={12} md={12}>
                                            <Grid item xs={12} md={12}>
                                                <Typography
                                                    sx={{
                                                        marginTop: 5,
                                                        marginBottom: 5,
                                                    }}
                                                    align="center"
                                                    variant="body1"
                                                    color="primary"
                                                >
                                                    Select users from{' '}
                                                    <strong>
                                                        {dealerProfileName}
                                                    </strong>{' '}
                                                    to receive dealer
                                                    notifications
                                                </Typography>
                                            </Grid>

                                            <Grid item xs={12} md={12}>
                                                <UniversalAutoComplete
                                                    filterOptions={(
                                                        options: any[]
                                                    ) => {
                                                        if (!options) return []

                                                        return options.filter(
                                                            (option: any) => {
                                                                return !dealerNotificationUsers?.some(
                                                                    (
                                                                        selected: any
                                                                    ) =>
                                                                        selected?._id ===
                                                                        option?._id
                                                                )
                                                            }
                                                        )
                                                    }}
                                                    getOptionLabel={(
                                                        option
                                                    ) => {
                                                        if (
                                                            !option ||
                                                            typeof option !==
                                                                'object'
                                                        ) {
                                                            return ''
                                                        }

                                                        const firstName =
                                                            option.userFirstName ||
                                                            ''
                                                        const lastName =
                                                            option.userLastName ||
                                                            ''
                                                        const email =
                                                            option.userEmail ||
                                                            ''

                                                        const fullName = [
                                                            firstName,
                                                            lastName,
                                                        ]
                                                            .filter(Boolean)
                                                            .join(' ')

                                                        return fullName
                                                            ? `${fullName} (${email})`
                                                            : email
                                                    }}
                                                    options={
                                                        dealerAssignedUsers ||
                                                        []
                                                    }
                                                    isOptionEqualToValue={(
                                                        option,
                                                        value
                                                    ) =>
                                                        option._id === value._id
                                                    }
                                                    handleChange={
                                                        handleUserChange
                                                    }
                                                    value={findUser}
                                                    name="dealerNotificationUsers"
                                                    onBlur={validateOnBlur}
                                                    fullWidth
                                                    placeholder="Enter an email name to search..."
                                                    noOptionsText={
                                                        'No Remaining Users Found  '
                                                    }
                                                    label={'Dealer Users'}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={12}>
                                                <List sx={{ mt: 2 }}>
                                                    {dealerNotificationUsers?.map(
                                                        (user) => (
                                                            <ListItem
                                                                key={user._id}
                                                                secondaryAction={
                                                                    <IconButton
                                                                        edge="end"
                                                                        title="Remove User"
                                                                        onClick={() =>
                                                                            onUserDelete(
                                                                                user._id
                                                                            )
                                                                        }
                                                                    >
                                                                        <DeleteIcon />
                                                                    </IconButton>
                                                                }
                                                            >
                                                                <ListItemAvatar>
                                                                    <Avatar>
                                                                        <PersonIcon />
                                                                    </Avatar>
                                                                </ListItemAvatar>
                                                                <ListItemText
                                                                    primary={
                                                                        user.userEmail
                                                                    }
                                                                    secondary={`${user?.userFirstName} ${user?.userLastName}`}
                                                                />
                                                            </ListItem>
                                                        )
                                                    )}
                                                </List>
                                            </Grid>
                                        </Grid>
                                    </FormSection>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>

                    <Grid item xs={5} md={5}>
                        <FormSection
                            title={`Assigned Dealers for ${dealerProfileName}`}
                        >
                            <Grid item xs={12} md={12}>
                                <Typography
                                    sx={{ marginTop: 5 }}
                                    align="center"
                                    variant="body1"
                                    color="primary"
                                >
                                    Select a Dealer to Link to this Profile
                                </Typography>
                            </Grid>

                            <Grid item xs={12} md={12}>
                                <UniversalAutoComplete
                                    options={dealerOptions}
                                    handleChange={onDealerQueryChange}
                                    disabled={!isAdmin}
                                    value={findDealer}
                                    name="dealers"
                                    onBlur={validateOnBlur}
                                    required={true}
                                    error={
                                        dealersValidation &&
                                        dealers?.length === 0
                                    }
                                    onInputChange={(e, newValue) => {
                                        debouncedSearch(newValue, 'dealerQuery')
                                    }}
                                    helperText={
                                        dealersValidation &&
                                        dealers?.length === 0
                                            ? 'Please Add a Dealer.'
                                            : ''
                                    }
                                    fullWidth
                                    placeholder="Enter a dealer name to search..."
                                    noOptionsText={
                                        'No matching dealers found - please try a different query'
                                    }
                                    label={'Find Dealer'}
                                />
                            </Grid>

                            <Grid item xs={12} md={12}>
                                <List sx={{ mt: 2 }}>
                                    {dealers.map((dealer) => (
                                        <ListItem
                                            key={dealer._id}
                                            secondaryAction={
                                                <IconButton
                                                    edge="end"
                                                    disabled={!isAdmin}
                                                    onClick={() =>
                                                        onDealerDelete(
                                                            dealer._id
                                                        )
                                                    }
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            }
                                        >
                                            <ListItemAvatar>
                                                <Avatar>
                                                    <MapsHomeWorkIcon />
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={dealer.dealerName}
                                                secondary={
                                                    dealer?.manufacturer
                                                        ?.map(
                                                            (m: any) =>
                                                                m.manufacturerName
                                                        )
                                                        .join(', ') || ''
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Grid>
                            {manufacturers?.length > 0 && (
                                <Grid item xs={12} md={12} lg={12}>
                                    <Grid container spacing={1}>
                                        {manufacturers.map((manufacturer) => (
                                            <Chip
                                                key={manufacturer._id}
                                                label={
                                                    manufacturer.manufacturerName
                                                }
                                                size="medium"
                                                variant="filled"
                                                icon={<CircleIcon />}
                                            />
                                        ))}
                                    </Grid>
                                </Grid>
                            )}
                        </FormSection>
                        {dealers?.length > 0 && (
                            <Grid item xs={12} md={12}>
                                {/* <UniversalAccordion
                                    accordionTitle={
                                        <Typography
                                            variant="body1"
                                            color="primary"
                                        >
                                            {dealerProfileName} Assigned Dealer
                                            Contact Information
                                        </Typography>
                                    }
                                    accordionStyle={{ color: '' }}
                                > */}
                                <FormSection>
                                    <Tabs
                                        value={safeTabValue}
                                        onChange={handleTabChange}
                                        aria-label="dealer tabs"
                                        variant="scrollable"
                                        scrollButtons="auto"
                                        sx={{
                                            borderBottom: 1,
                                            borderColor: 'divider',
                                            mt: -1,
                                        }}
                                    >
                                        {dealers.map((dealer, index) => (
                                            <Tab
                                                key={dealer?._id}
                                                label={dealer?.dealerName}
                                                id={`dealer-tab-${index}`}
                                            />
                                        ))}
                                    </Tabs>

                                    {dealers.map((dealer, index) => (
                                        <CustomTabPanel
                                            key={dealer._id}
                                            value={safeTabValue}
                                            index={index}
                                        >
                                            <Grid item xs={12} md={12}>
                                                <Typography
                                                    sx={{ marginBottom: 5 }}
                                                    align="center"
                                                    variant="h5"
                                                    color="primary"
                                                >
                                                    {dealer?.dealerName} Contact
                                                    Information
                                                </Typography>
                                            </Grid>

                                            <Grid container spacing={2}>
                                                <Grid item xs={12}>
                                                    <Grid container spacing={2}>
                                                        <Grid
                                                            item
                                                            xs={11}
                                                            md={11}
                                                        >
                                                            <TextField
                                                                label="Dealer Name"
                                                                value={
                                                                    dealer?.dealerName
                                                                }
                                                                fullWidth
                                                                disabled={true}
                                                            />
                                                        </Grid>
                                                        <Grid
                                                            item
                                                            xs={1}
                                                            md={1}
                                                        >
                                                            <Button
                                                                title={`Add ${dealer?.dealerName} to primary contact dealer name`}
                                                                onClick={() =>
                                                                    setState({
                                                                        formData:
                                                                            {
                                                                                ...formData,
                                                                                dealerName:
                                                                                    dealer?.dealerName,
                                                                            },
                                                                        formValidation:
                                                                            {
                                                                                ...formValidation,
                                                                                isFormDirty:
                                                                                    true,
                                                                            },
                                                                    })
                                                                }
                                                                startIcon={
                                                                    <AddBoxIcon />
                                                                }
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <Grid container spacing={2}>
                                                        <Grid
                                                            item
                                                            xs={11}
                                                            md={11}
                                                        >
                                                            <TextField
                                                                label="Email"
                                                                value={
                                                                    dealer?.dealerEmail
                                                                }
                                                                fullWidth
                                                                disabled={true}
                                                            />
                                                        </Grid>
                                                        <Grid
                                                            item
                                                            xs={1}
                                                            md={1}
                                                        >
                                                            <Button
                                                                title={`Add ${dealer?.dealerEmail} to primary contact email`}
                                                                onClick={() =>
                                                                    setState({
                                                                        formData:
                                                                            {
                                                                                ...formData,
                                                                                dealerEmail:
                                                                                    dealer?.dealerEmail,
                                                                            },
                                                                        formValidation:
                                                                            {
                                                                                ...formValidation,
                                                                                isFormDirty:
                                                                                    true,
                                                                            },
                                                                    })
                                                                }
                                                                startIcon={
                                                                    <AddBoxIcon />
                                                                }
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Grid container spacing={2}>
                                                        <Grid
                                                            item
                                                            xs={11}
                                                            md={11}
                                                        >
                                                            <TextField
                                                                label="Dealer Phone Number"
                                                                value={
                                                                    dealer?.dealerPhoneNumber
                                                                }
                                                                fullWidth
                                                                disabled={true}
                                                            />
                                                        </Grid>
                                                        <Grid
                                                            item
                                                            xs={1}
                                                            md={1}
                                                        >
                                                            <Button
                                                                title={`Add ${dealer?.dealerPhoneNumber} to primary contact phone number`}
                                                                onClick={() =>
                                                                    setState({
                                                                        formData:
                                                                            {
                                                                                ...formData,
                                                                                dealerPhoneNumber:
                                                                                    dealer?.dealerPhoneNumber,
                                                                            },
                                                                        formValidation:
                                                                            {
                                                                                ...formValidation,
                                                                                isFormDirty:
                                                                                    true,
                                                                            },
                                                                    })
                                                                }
                                                                startIcon={
                                                                    <AddBoxIcon />
                                                                }
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Grid container spacing={2}>
                                                        <Grid
                                                            item
                                                            xs={11}
                                                            md={11}
                                                        >
                                                            <TextField
                                                                label="Sales Contact Name"
                                                                value={
                                                                    dealer?.dealerSalesContactName
                                                                }
                                                                fullWidth
                                                                disabled={true}
                                                            />
                                                        </Grid>
                                                        <Grid
                                                            item
                                                            xs={1}
                                                            md={1}
                                                        >
                                                            <Button
                                                                title={`Add ${dealer?.dealerSalesContactName} to primary sales contact name`}
                                                                onClick={() =>
                                                                    setState({
                                                                        formData:
                                                                            {
                                                                                ...formData,
                                                                                dealerSalesContactName:
                                                                                    dealer?.dealerSalesContactName,
                                                                            },
                                                                        formValidation:
                                                                            {
                                                                                ...formValidation,
                                                                                isFormDirty:
                                                                                    true,
                                                                            },
                                                                    })
                                                                }
                                                                startIcon={
                                                                    <AddBoxIcon />
                                                                }
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <Grid container spacing={2}>
                                                        <Grid
                                                            item
                                                            xs={11}
                                                            md={11}
                                                        >
                                                            <TextField
                                                                label="Sales Contact Email"
                                                                value={
                                                                    dealer?.dealerSalesContactEmail
                                                                }
                                                                fullWidth
                                                                disabled={true}
                                                            />
                                                        </Grid>
                                                        <Grid
                                                            item
                                                            xs={1}
                                                            md={1}
                                                        >
                                                            <Button
                                                                title={`Add ${dealer?.dealerSalesContactEmail} to primary sales contact email`}
                                                                onClick={() =>
                                                                    setState({
                                                                        formData:
                                                                            {
                                                                                ...formData,
                                                                                dealerSalesContactEmail:
                                                                                    dealer?.dealerSalesContactEmail,
                                                                            },
                                                                        formValidation:
                                                                            {
                                                                                ...formValidation,
                                                                                isFormDirty:
                                                                                    true,
                                                                            },
                                                                    })
                                                                }
                                                                startIcon={
                                                                    <AddBoxIcon />
                                                                }
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Grid container spacing={2}>
                                                        <Grid
                                                            item
                                                            xs={11}
                                                            md={11}
                                                        >
                                                            <TextField
                                                                label="Sales Contact Phone"
                                                                value={
                                                                    dealer?.dealerSalesContactPhone
                                                                }
                                                                fullWidth
                                                                disabled={true}
                                                            />
                                                        </Grid>
                                                        <Grid
                                                            item
                                                            xs={1}
                                                            md={1}
                                                        >
                                                            <Button
                                                                title={`Add ${dealer?.dealerSalesContactPhone} to primary sales contact phone number`}
                                                                onClick={() =>
                                                                    setState({
                                                                        formData:
                                                                            {
                                                                                ...formData,
                                                                                dealerSalesContactPhone:
                                                                                    dealer?.dealerSalesContactPhone,
                                                                            },
                                                                        formValidation:
                                                                            {
                                                                                ...formValidation,
                                                                                isFormDirty:
                                                                                    true,
                                                                            },
                                                                    })
                                                                }
                                                                startIcon={
                                                                    <AddBoxIcon />
                                                                }
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Grid container spacing={2}>
                                                        <Grid
                                                            item
                                                            xs={11}
                                                            md={11}
                                                        >
                                                            <TextField
                                                                label="Service Contact Name"
                                                                value={
                                                                    dealer?.dealerServiceContactName
                                                                }
                                                                fullWidth
                                                                disabled={true}
                                                            />
                                                        </Grid>
                                                        <Grid
                                                            item
                                                            xs={1}
                                                            md={1}
                                                        >
                                                            <Button
                                                                title={`Add ${dealer?.dealerServiceContactName} to primary service contact name`}
                                                                onClick={() =>
                                                                    setState({
                                                                        formData:
                                                                            {
                                                                                ...formData,
                                                                                dealerServiceContactName:
                                                                                    dealer?.dealerServiceContactName,
                                                                            },
                                                                        formValidation:
                                                                            {
                                                                                ...formValidation,
                                                                                isFormDirty:
                                                                                    true,
                                                                            },
                                                                    })
                                                                }
                                                                startIcon={
                                                                    <AddBoxIcon />
                                                                }
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Grid container spacing={2}>
                                                        <Grid
                                                            item
                                                            xs={11}
                                                            md={11}
                                                        >
                                                            <TextField
                                                                label="Service Contact Email"
                                                                value={
                                                                    dealer?.dealerServiceContactEmail
                                                                }
                                                                fullWidth
                                                                disabled={true}
                                                            />
                                                        </Grid>
                                                        <Grid
                                                            item
                                                            xs={1}
                                                            md={1}
                                                        >
                                                            <Button
                                                                title={`Add ${dealer?.dealerServiceContactEmail} to primary service contact email`}
                                                                onClick={() =>
                                                                    setState({
                                                                        formData:
                                                                            {
                                                                                ...formData,
                                                                                dealerServiceContactEmail:
                                                                                    dealer?.dealerServiceContactEmail,
                                                                            },
                                                                        formValidation:
                                                                            {
                                                                                ...formValidation,
                                                                                isFormDirty:
                                                                                    true,
                                                                            },
                                                                    })
                                                                }
                                                                startIcon={
                                                                    <AddBoxIcon />
                                                                }
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Grid container spacing={2}>
                                                        <Grid
                                                            item
                                                            xs={11}
                                                            md={11}
                                                        >
                                                            <TextField
                                                                label="Service Contact Phone"
                                                                value={
                                                                    dealer?.dealerServiceContactPhone
                                                                }
                                                                fullWidth
                                                                disabled={true}
                                                            />
                                                        </Grid>
                                                        <Grid
                                                            item
                                                            xs={1}
                                                            md={1}
                                                        >
                                                            <Button
                                                                title={`Add ${dealer?.dealerServiceContactPhone} to primary service contact phone`}
                                                                onClick={() =>
                                                                    setState({
                                                                        formData:
                                                                            {
                                                                                ...formData,
                                                                                dealerServiceContactPhone:
                                                                                    dealer?.dealerServiceContactPhone,
                                                                            },
                                                                        formValidation:
                                                                            {
                                                                                ...formValidation,
                                                                                isFormDirty:
                                                                                    true,
                                                                            },
                                                                    })
                                                                }
                                                                startIcon={
                                                                    <AddBoxIcon />
                                                                }
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </CustomTabPanel>
                                    ))}
                                </FormSection>
                                {/* </UniversalAccordion> */}
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Grid>

            <Grid item xs={12}>
                <FormSection
                    title="Required fields are marked with an * "
                    titleAlignment="center"
                >
                    <Grid item xs={7}>
                        <Button
                            onClick={handleSubmit}
                            variant="contained"
                            color="primary"
                            fullWidth
                            sx={{
                                width: '100%',
                                height: 55,
                            }}
                            disabled={!isFormValid}
                        >
                            Save Dealer
                        </Button>
                    </Grid>
                    <Grid item xs={5}>
                        <Button
                            onClick={handleReset}
                            variant={isFormValid ? 'outlined' : 'contained'}
                            color="error"
                            disabled={!isFormDirty}
                            sx={{
                                width: '100%',
                                height: 55,
                            }}
                            fullWidth
                        >
                            Reset
                        </Button>
                    </Grid>
                </FormSection>
            </Grid>
        </Grid>
    )
}

export default DealerProfileForm
